/**
 * Role-based access control.
 *
 * Ported from the previous build's `src/data/permissions.js`, which had a
 * well-shaped role/module/action matrix with per-role scopes — and three
 * problems that this version fixes:
 *
 *   1. It ran only in the browser. The Firestore rules behind it allowed any
 *      authenticated user to read and write every collection, so the matrix was
 *      advisory. A parent with a developer console could edit grades. Here the
 *      same matrix is evaluated in the API, before any container is touched.
 *
 *   2. `canAccessRoute()` ended in `return true` for every authenticated user,
 *      so route protection was a no-op.
 *
 *   3. The `scope` on each role was declared but never enforced. A teacher's
 *      scope was `assigned_classes`, yet nothing narrowed a read to the classes
 *      they teach. Scope is now a required part of every authorization decision
 *      (see `resolveRecordScope`), because a module-level yes/no cannot express
 *      "may read this student but not that one" — which is most of what an SIS
 *      access model has to say.
 */

export const ROLES = {
  SYSTEM_ADMIN: 'system_admin',
  IT_ADMIN: 'it_admin',
  DISTRICT_ADMIN: 'district_admin',
  SCHOOL_ADMIN: 'school_admin',
  PRINCIPAL: 'principal',
  ASSISTANT_PRINCIPAL: 'assistant_principal',
  TEACHER: 'teacher',
  SUBSTITUTE_TEACHER: 'substitute_teacher',
  ADMINISTRATIVE_STAFF: 'administrative_staff',
  ATTENDANCE_CLERK: 'attendance_clerk',
  BILLING_ENROLLMENT_ADMIN: 'billing_enrollment_admin',
  SCHOOL_COUNSELOR: 'school_counselor',
  REGISTRAR: 'registrar',
  /** EMIS / state reporting coordinator: the district's state reporting, end to end. */
  EMIS_COORDINATOR: 'emis_coordinator',
  /** Treasurer: finance and the five-year forecast for state reporting. */
  TREASURER: 'treasurer',
  /** Human Resources: the personnel record — positions, credentials, contracts, documents, accounts. */
  HR: 'human_resources',
  /** Payroll: compensation and payroll exports; nothing else personal. */
  PAYROLL: 'payroll',
  IEP_COORDINATOR: 'iep_coordinator',
  /** Runs testing: windows, sessions, rosters, accommodations, makeups, scoring review and release. */
  ASSESSMENT_COORDINATOR: 'assessment_coordinator',
  /** District curriculum: manages frameworks, maps, materials; the role that publishes. */
  CURRICULUM_DIRECTOR: 'curriculum_director',
  SPECIAL_SERVICES: 'special_services',
  TRANSPORTATION_OPERATIONS: 'transportation_operations',
  /** District operations: the daily command center across schools. */
  OPERATIONS_MANAGER: 'operations_manager',
  /** Custodial and facilities staff: complete checks and tasks, update work orders. */
  FACILITIES_STAFF: 'facilities_staff',
  /** Facilities management: triage, assignment, priorities, vendors, PM, inspections, settings — across schools. */
  FACILITIES_MANAGER: 'facilities_manager',
  BUS_DRIVER: 'bus_driver',
  /** District property: the full registry, retirement and disposal, financial fields, settings. */
  ASSET_MANAGER: 'asset_manager',
  /** School technology: issue and collect devices, repairs, labels, inventory. */
  TECHNOLOGY_STAFF: 'technology_staff',
  LIBRARY_MEDIA_SPECIALIST: 'library_media_specialist',
  /** Front-desk circulation: checkout, return, holds, patron lookup, labels. */
  LIBRARY_ASSISTANT: 'library_assistant',
  MEDICAL_STAFF: 'medical_staff',
  STUDENT: 'student',
  PARENT_GUARDIAN: 'parent_guardian',
  /**
   * An invited focus-group tester.
   *
   * Their *professional* role — teacher, principal, registrar — is recorded on
   * their tester profile, because that is what the feedback has to be analysed
   * by. This is a separate thing: what their account may do while touring the
   * product. Conflating the two would mean a tester who is a teacher in real
   * life sees nothing in the demo school, since a teacher is scoped to the
   * sections they teach and they teach none of them.
   *
   * Read-only across the school, by grant rather than by a flag checked
   * somewhere: there is no action on this role that writes a school record.
   */
  FOCUS_GROUP_TESTER: 'focus_group_tester',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

export const ROLE_LABELS: Record<Role, string> = {
  // "Developer" rather than "System Administrator": the people holding this
  // role build the product, and every school has a system administrator of
  // its own. Calling both the same thing made the operator indistinguishable
  // from a customer's IT lead in a user list and an audit log.
  system_admin: 'Developer',
  it_admin: 'IT Administrator',
  district_admin: 'District Administrator',
  school_admin: 'School Administrator',
  principal: 'Principal',
  assistant_principal: 'Assistant Principal',
  teacher: 'Teacher',
  substitute_teacher: 'Substitute Teacher',
  administrative_staff: 'Administrative Staff',
  attendance_clerk: 'Attendance Clerk',
  billing_enrollment_admin: 'Billing & Enrollment Administrator',
  school_counselor: 'School Counselor',
  registrar: 'Registrar',
  emis_coordinator: 'EMIS Coordinator',
  treasurer: 'Treasurer',
  human_resources: 'Human Resources',
  payroll: 'Payroll',
  iep_coordinator: 'IEP / Special Education Coordinator',
  assessment_coordinator: 'Assessment / Testing Coordinator',
  curriculum_director: 'Curriculum Director',
  special_services: 'Special Services',
  transportation_operations: 'Transportation Coordinator',
  operations_manager: 'Operations Manager',
  facilities_staff: 'Facilities Staff',
  facilities_manager: 'Facilities Manager',
  bus_driver: 'Bus Driver',
  asset_manager: 'Asset Manager',
  technology_staff: 'Technology Staff',
  library_media_specialist: 'Library / Media Specialist',
  library_assistant: 'Library Assistant',
  medical_staff: 'Nurse / Medical Staff',
  student: 'Student',
  parent_guardian: 'Parent / Guardian',
  focus_group_tester: 'Focus Group Tester',
}

/**
 * How widely a role may see.
 *
 * This is the half of the model that a module/action matrix cannot express, and
 * the half that matters most in a school: almost every role can "view students",
 * and they differ entirely in *which* students.
 */
export const SCOPES = {
  /** Every tenant. Reserved for platform operations. */
  PLATFORM: 'platform',
  /** Every school in the tenant's district. */
  DISTRICT: 'district',
  /** One school. */
  SCHOOL: 'school',
  /** Only students rostered into sections the user teaches. */
  ASSIGNED_SECTIONS: 'assigned_sections',
  /** Only students on an explicit caseload. Counselors, IEP, special services. */
  CASELOAD: 'caseload',
  /** Only sections covered by a dated substitute assignment. */
  TEMPORARY_ASSIGNMENT: 'temporary_assignment',
  /** Only students the user is a recorded guardian of. */
  HOUSEHOLD: 'household',
  /** Only the user's own record. */
  SELF: 'self',
} as const

export type Scope = (typeof SCOPES)[keyof typeof SCOPES]

export const ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  APPROVE: 'approve',
  OVERRIDE: 'override',
  EXPORT: 'export',
  MESSAGE: 'message',
  UPLOAD: 'upload',
  AUDIT_VIEW: 'auditView',
  PRINT: 'print',
  IMPERSONATE: 'impersonate',
} as const

export type Action = (typeof ACTIONS)[keyof typeof ACTIONS]

/**
 * Modules are the permission surface. One per coherent area of the product —
 * coarse enough to stay reviewable, fine enough that "a nurse may not read
 * discipline records" is expressible.
 */
export const MODULES = [
  'systemSettings',
  'tenants',
  'districts',
  'schools',
  'users',
  'roles',
  'branding',
  'admissions',
  'forms',
  'students',
  'studentDemographics',
  'guardians',
  'households',
  'staff',
  'staffEvaluations',
  'enrollment',
  'applications',
  'courses',
  'sections',
  'schedules',
  'courseRequests',
  'masterSchedule',
  'attendance',
  'gradebook',
  'assignments',
  'grades',
  'gradeOverrides',
  'transcripts',
  'credits',
  'graduation',
  'assessments',
  'curriculum',
  'absences',
  'activities',
  'emergency',
  'facilities',
  'purchasing',
  // ── Finance ───────────────────────────────────────────────────────────────
  // Split by the work rather than by the screen, because the separations
  // that matter here are between jobs: a clerk who enters invoices is not a
  // director who approves payment, and a department head who asks for budget
  // is not the office that amends it.
  /** The module front door: dashboard, funds, accounts, reports. */
  'finance',
  /** Budget lines, amendments and transfers — spending authority itself. */
  'financeBudget',
  /** Budget requests: asking for authority, and deciding on the asks. */
  'financeRequests',
  /** Vendors, purchase orders, invoices, payments and batches. */
  'financePayables',
  /** The general ledger and journal entries. */
  'financeLedger',
  /** Grants and restricted funds. */
  'financeGrants',
  /** Bank reconciliation and fiscal-year close. */
  'financeReconciliation',
  /** Finance configuration: tolerances, chains, segregation rules. */
  'financeSettings',
  'stateReporting',
  'visitors',
  /** Operations: readiness, checklists, issues, tasks, event support, deliveries, inspections. */
  'operations',
  'evaluations',
  'assets',
  'cafeteria',
  'discipline',
  'counseling',
  'sessionNotes',
  'medical',
  'medications',
  'immunizations',
  'iep',
  'interventions',
  'documents',
  'fees',
  'billing',
  'payments',
  'pricing',
  'transportation',
  /** Dismissal / Pickup: release plans, changes, pickup and the daily release record. */
  'dismissal',
  'library',
  'calendar',
  'announcements',
  'messages',
  'notifications',
  'reports',
  'customReports',
  'printCenter',
  'dataImport',
  'dataExport',
  'auditLogs',
  'integrations',
  'helper',
  // ── Focus group programme ─────────────────────────────────────────────────
  /** A tester's own feedback, ideas, missions and history. */
  'feedbackLab',
  /** Staff triage: the inbox, themes, priorities, analytics. */
  'feedbackAdmin',
  /** Tester profiles, cohorts, invitations and reward eligibility. */
  'focusGroup',
  'ownProfile',
  'ownGrades',
  'ownAttendance',
  'ownAssignments',
  'ownSchedule',
  'childProfile',
  'childGrades',
  'childAttendance',
  'childAssignments',
  'childDocuments',
] as const

export type Module = (typeof MODULES)[number]

const ALL: readonly Action[] = [
  ACTIONS.VIEW,
  ACTIONS.CREATE,
  ACTIONS.EDIT,
  ACTIONS.APPROVE,
  ACTIONS.OVERRIDE,
  ACTIONS.EXPORT,
  ACTIONS.MESSAGE,
  ACTIONS.UPLOAD,
  ACTIONS.PRINT,
]
const VIEW_ONLY: readonly Action[] = [ACTIONS.VIEW, ACTIONS.PRINT]
const VIEW_PRINT_EXPORT: readonly Action[] = [ACTIONS.VIEW, ACTIONS.PRINT, ACTIONS.EXPORT]
const MANAGE: readonly Action[] = [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT]
const CORRESPOND: readonly Action[] = [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.MESSAGE]

/**
 * The modules that describe a person's own record, or their child's.
 *
 * These are the family portal's surface and they resolve against the
 * principal's own `studentId` or `guardianStudentIds`. A staff account has
 * neither, so granting them to a staff role would be decoration: the grant
 * would pass and then resolve to nothing. Excluded from the superuser grant
 * for that reason, not as a restriction.
 */
const SELF_SERVICE_MODULES: ReadonlySet<Module> = new Set<Module>([
  'ownProfile',
  'ownGrades',
  'ownAttendance',
  'ownAssignments',
  'ownSchedule',
  'childProfile',
  'childGrades',
  'childAttendance',
  'childAssignments',
  'childDocuments',
])

/**
 * Every action on every module a staff account can meaningfully hold.
 *
 * Derived from `MODULES` rather than written out. A hand-written list of
 * sixty-odd modules would go stale the first time one was added, and for the
 * single role whose job is to be able to reach everything, a silently missing
 * module is the failure mode that would survive longest — the symptom is a
 * blank page months later, not a test failure.
 *
 * `DELETE` and `IMPERSONATE` are deliberately absent. No role is granted
 * either and no route checks either: records are retired by `recordStatus`
 * rather than deleted, and while a session can carry an impersonated user,
 * nothing yet starts one. Granting an action no code consults would make this
 * role's permissions describe capabilities the product does not have.
 */
const EVERYTHING: Partial<Record<Module, readonly Action[]>> = Object.fromEntries(
  MODULES.filter((module) => !SELF_SERVICE_MODULES.has(module)).map((module) => [
    module,
    [...ALL, ACTIONS.AUDIT_VIEW] as readonly Action[],
  ]),
)

export interface RoleDefinition {
  scope: Scope
  modules: Partial<Record<Module, readonly Action[]>>
  /**
   * Confidential areas a role may reach regardless of module grants. Used to
   * keep counseling notes and full medical records away from roles that can
   * otherwise see a great deal — a principal included.
   */
  confidential?: readonly ('counselingNotes' | 'medicalDetail' | 'disciplineDetail' | 'iepDetail' | 'financeSensitive' | 'staffPersonnel' | 'staffCompensation')[]
}

export const ROLE_PERMISSIONS: Record<Role, RoleDefinition> = {
  /**
   * The operator's own role. Unlike every other entry here, it is defined by
   * what it cannot be stopped from doing.
   *
   * It held only the platform modules — tenants, users, roles, settings,
   * integrations — which was a coherent position and an unusable one: none of
   * those modules maps to a navigation item, so the role signed in to an
   * application with nothing in it. Worse, it was unusable in a way that
   * looked deliberate, so the first person to hold the role would have
   * reported the product broken rather than the role misconfigured.
   *
   * It is now a genuine superuser, scoped to the platform and granted every
   * module. That is a real transfer of reach over student records, and the
   * reason it is acceptable here and nowhere else is that `system_admin` is
   * held by the people who operate the service rather than by anyone a school
   * employs — a school administrator still cannot read a counseling note. The
   * accountability is the audit log, which records this role's reads as well
   * as its writes, not a narrower grant.
   */
  [ROLES.SYSTEM_ADMIN]: {
    scope: SCOPES.PLATFORM,
    modules: EVERYTHING,
    confidential: ['counselingNotes', 'medicalDetail', 'disciplineDetail', 'iepDetail', 'financeSensitive', 'staffPersonnel', 'staffCompensation'],
  },

  // Deliberately *not* a superuser over student data. IT administers the
  // platform; it does not need to read a child's health record to do that.
  [ROLES.IT_ADMIN]: {
    scope: SCOPES.DISTRICT,
    modules: {
      calendar: [ACTIONS.VIEW],
      systemSettings: ALL,
      users: ALL,
      roles: ALL,
      schools: ALL,
      districts: ALL,
      branding: ALL,
      integrations: ALL,
      assets: ALL,
      facilities: MANAGE,
      dataImport: ALL,
      dataExport: VIEW_PRINT_EXPORT,
      stateReporting: VIEW_ONLY,
      auditLogs: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
    },
  },

  [ROLES.DISTRICT_ADMIN]: {
    scope: SCOPES.DISTRICT,
    modules: {
      // Documents: the district's records officer — retention, legal holds, disposition.
      documents: ALL,
      // Administration → Families.
      guardians: ALL,
      households: ALL,
      // Administration → Users.
      users: ALL,
      operations: ALL,
      dismissal: ALL,
      calendar: ALL,
      // The CFO / treasurer seat: the district's whole financial position,
      // and the executive approval the chain's top step waits on.
      finance: ALL,
      financeBudget: ALL,
      financeRequests: ALL,
      financePayables: ALL,
      financeLedger: ALL,
      financeGrants: ALL,
      financeReconciliation: ALL,
      financeSettings: [ACTIONS.VIEW, ACTIONS.EDIT],
      schools: VIEW_ONLY,
      students: VIEW_PRINT_EXPORT,
      staff: VIEW_PRINT_EXPORT,
      absences: VIEW_PRINT_EXPORT,
      emergency: VIEW_PRINT_EXPORT,
      evaluations: VIEW_PRINT_EXPORT,
      staffEvaluations: VIEW_ONLY,
      enrollment: VIEW_PRINT_EXPORT,
      courses: VIEW_PRINT_EXPORT,
      sections: VIEW_PRINT_EXPORT,
      attendance: VIEW_PRINT_EXPORT,
      grades: VIEW_PRINT_EXPORT,
      graduation: VIEW_PRINT_EXPORT,
      discipline: [ACTIONS.VIEW, ACTIONS.PRINT],
      stateReporting: ALL,
      activities: VIEW_PRINT_EXPORT,
      cafeteria: VIEW_PRINT_EXPORT,
      assets: VIEW_PRINT_EXPORT,
      interventions: VIEW_ONLY,
      billing: VIEW_PRINT_EXPORT,
      pricing: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.APPROVE],
      reports: VIEW_PRINT_EXPORT,
      customReports: ALL,
      announcements: [ACTIONS.VIEW, ACTIONS.CREATE],
      auditLogs: VIEW_ONLY,
      printCenter: VIEW_ONLY,
      helper: [ACTIONS.VIEW],
    },
    // Bank references and vendor tax data: the treasurer's own business.
    confidential: ['financeSensitive'],
  },

  [ROLES.SCHOOL_ADMIN]: {
    scope: SCOPES.SCHOOL,
    modules: {
      // Administration → Users.
      users: [ACTIONS.VIEW],
      operations: ALL,
      // The finance director's desk. Everything in the module, including
      // the configuration that decides tolerances and the segregation rules
      // — which is why changing those is audited.
      finance: ALL,
      financeBudget: ALL,
      financeRequests: ALL,
      financePayables: ALL,
      financeLedger: ALL,
      financeGrants: ALL,
      financeReconciliation: ALL,
      financeSettings: ALL,
      transportation: [...MANAGE, ACTIONS.APPROVE],
      dismissal: [...ALL],
      curriculum: [...MANAGE, ACTIONS.APPROVE],
      assessments: [...MANAGE, ACTIONS.APPROVE],
      courseRequests: [...MANAGE, ACTIONS.APPROVE],
      fees: [...MANAGE, ACTIONS.APPROVE],
      billing: [...MANAGE, ACTIONS.APPROVE],
      forms: [...MANAGE, ACTIONS.APPROVE],
      // Accepting an applicant is the head of school’s call here.
      admissions: [...MANAGE, ACTIONS.APPROVE],
      schools: MANAGE,
      branding: MANAGE,
      students: MANAGE,
      studentDemographics: MANAGE,
      guardians: MANAGE,
      households: MANAGE,
      // MANAGE rather than view-only: running staff development — creating PD
      // events, confirming attendance, crediting hours to licensure records —
      // is school administration, and before this grant no role a school
      // actually holds could reach any of it.
      staff: MANAGE,
      absences: ALL,
      emergency: ALL,
      facilities: ALL,
      purchasing: ALL,
      stateReporting: ALL,
      visitors: ALL,
      library: ALL,
      evaluations: ALL,
      enrollment: ALL,
      courses: ALL,
      sections: ALL,
      schedules: ALL,
      masterSchedule: ALL,
      attendance: [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.OVERRIDE, ACTIONS.PRINT, ACTIONS.EXPORT],
      gradebook: VIEW_ONLY,
      // OVERRIDE is what reopens grade entry after a grading period closes.
      // Without it on at least one role, a late or corrected grade could never
      // be posted at all — the window would be an absolute wall.
      grades: [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.PRINT, ACTIONS.EXPORT, ACTIONS.APPROVE, ACTIONS.OVERRIDE],
      gradeOverrides: [ACTIONS.VIEW, ACTIONS.APPROVE],
      transcripts: VIEW_PRINT_EXPORT,
      graduation: [...VIEW_PRINT_EXPORT, ACTIONS.APPROVE],
      discipline: ALL,
      activities: ALL,
      cafeteria: ALL,
      assets: ALL,
      interventions: ALL,
      documents: ALL,
      calendar: ALL,
      announcements: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT],
      messages: CORRESPOND,
      reports: VIEW_PRINT_EXPORT,
      customReports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      // Importing the roster at the start of a year is a school
      // administrator's job, not an IT task.
      dataImport: [ACTIONS.VIEW, ACTIONS.CREATE],
      dataExport: VIEW_PRINT_EXPORT,
      auditLogs: VIEW_ONLY,
      helper: [ACTIONS.VIEW],
    },
    // A principal sees that a counseling case exists, not what was said in it.
    confidential: ['disciplineDetail'],
  },

  [ROLES.PRINCIPAL]: {
    scope: SCOPES.SCHOOL,
    modules: {
      documents: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.APPROVE, ACTIONS.UPLOAD, ACTIONS.PRINT],
      // Families: building leaders read the family setup, correct it, and verify restrictions.
      guardians: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.APPROVE, ACTIONS.PRINT],
      households: MANAGE,
      // State reporting: the findings this role corrects at their source.
      stateReporting: VIEW_ONLY,
      operations: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT],
      fees: [ACTIONS.VIEW, ACTIONS.PRINT],
      calendar: ALL,
      // A building leader reads their school's budgets and decides the
      // requests that climb through them. They do not issue district money:
      // no payables, no ledger, no reconciliation.
      finance: VIEW_PRINT_EXPORT,
      financeBudget: VIEW_PRINT_EXPORT,
      financeRequests: [...MANAGE, ACTIONS.APPROVE],
      students: VIEW_PRINT_EXPORT,
      staff: VIEW_PRINT_EXPORT,
      // The building leader sits in admissions review chains (never decides
      // alone — decisions stay with APPROVE holders).
      admissions: VIEW_ONLY,
      absences: ALL,
      emergency: ALL,
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.EDIT],
      visitors: VIEW_PRINT_EXPORT,
      library: VIEW_PRINT_EXPORT,
      // Reading who may pick a child up is the building leader's business;
      // managing routes stays with the office.
      transportation: [ACTIONS.VIEW],
      dismissal: [...ALL],
      evaluations: ALL,
      staffEvaluations: ALL,
      enrollment: VIEW_PRINT_EXPORT,
      sections: VIEW_PRINT_EXPORT,
      masterSchedule: VIEW_ONLY,
      attendance: [ACTIONS.VIEW, ACTIONS.OVERRIDE, ACTIONS.PRINT, ACTIONS.EXPORT],
      grades: [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.PRINT, ACTIONS.EXPORT, ACTIONS.APPROVE, ACTIONS.OVERRIDE],
      gradeOverrides: [ACTIONS.VIEW, ACTIONS.APPROVE],
      discipline: ALL,
      activities: ALL,
      cafeteria: VIEW_PRINT_EXPORT,
      assets: VIEW_PRINT_EXPORT,
      interventions: ALL,
      graduation: VIEW_PRINT_EXPORT,
      reports: VIEW_PRINT_EXPORT,
      announcements: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT],
      messages: CORRESPOND,
      printCenter: VIEW_ONLY,
      auditLogs: VIEW_ONLY,
      helper: [ACTIONS.VIEW],
    },
    confidential: ['disciplineDetail'],
  },

  [ROLES.ASSISTANT_PRINCIPAL]: {
    scope: SCOPES.SCHOOL,
    modules: {
      documents: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.APPROVE, ACTIONS.UPLOAD, ACTIONS.PRINT],
      guardians: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.APPROVE, ACTIONS.PRINT],
      households: MANAGE,
      // State reporting: the findings this role corrects at their source.
      stateReporting: VIEW_ONLY,
      operations: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT],
      fees: [ACTIONS.VIEW, ACTIONS.PRINT],
      calendar: ALL,
      // Stands in for the department-head step of the budget chain.
      finance: VIEW_ONLY,
      financeBudget: VIEW_ONLY,
      financeRequests: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.APPROVE],
      students: VIEW_PRINT_EXPORT,
      attendance: [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.OVERRIDE, ACTIONS.PRINT],
      discipline: ALL,
      absences: ALL,
      emergency: ALL,
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.EDIT],
      visitors: VIEW_PRINT_EXPORT,
      // Reading who may pick a child up is the building leader's business;
      // managing routes stays with the office.
      transportation: [ACTIONS.VIEW],
      dismissal: [...MANAGE, ACTIONS.APPROVE],
      evaluations: ALL,
      activities: ALL,
      cafeteria: VIEW_PRINT_EXPORT,
      interventions: ALL,
      grades: VIEW_PRINT_EXPORT,
      sections: VIEW_ONLY,
      reports: VIEW_PRINT_EXPORT,
      messages: CORRESPOND,
      printCenter: VIEW_ONLY,
    },
    confidential: ['disciplineDetail'],
  },

  [ROLES.TEACHER]: {
    scope: SCOPES.ASSIGNED_SECTIONS,
    modules: {
      transportation: [ACTIONS.VIEW],
      dismissal: [ACTIONS.VIEW, ACTIONS.EDIT],
      curriculum: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT],
      assessments: [ACTIONS.VIEW, ACTIONS.EDIT],
      courseRequests: [ACTIONS.VIEW, ACTIONS.CREATE],
      forms: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.APPROVE],
      students: VIEW_ONLY,
      sections: VIEW_PRINT_EXPORT,
      schedules: VIEW_ONLY,
      attendance: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT],
      gradebook: ALL,
      // A teacher files referrals and may know an incident exists; the
      // narrative stays behind disciplineDetail and the queue behind EDIT.
      discipline: [ACTIONS.VIEW, ACTIONS.CREATE],
      activities: [ACTIONS.VIEW, ACTIONS.EDIT],
      assignments: ALL,
      grades: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT],
      gradeOverrides: [ACTIONS.VIEW, ACTIONS.CREATE],
      interventions: [ACTIONS.VIEW, ACTIONS.CREATE],
      documents: [ACTIONS.VIEW, ACTIONS.UPLOAD],
      messages: CORRESPOND,
      calendar: [ACTIONS.VIEW, ACTIONS.CREATE],
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      evaluations: [ACTIONS.VIEW],
      library: VIEW_ONLY,
    },
  },

  [ROLES.SUBSTITUTE_TEACHER]: {
    scope: SCOPES.TEMPORARY_ASSIGNMENT,
    modules: {
      calendar: [ACTIONS.VIEW],
      sections: VIEW_ONLY,
      students: [ACTIONS.VIEW],
      dismissal: [ACTIONS.VIEW, ACTIONS.EDIT],
      attendance: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      assignments: VIEW_ONLY,
      printCenter: VIEW_ONLY,
      ownProfile: VIEW_ONLY,
    },
  },

  [ROLES.ADMINISTRATIVE_STAFF]: {
    scope: SCOPES.SCHOOL,
    modules: {
      operations: [ACTIONS.VIEW, ACTIONS.CREATE],
      transportation: [...MANAGE, ACTIONS.APPROVE],
      dismissal: [...MANAGE, ACTIONS.APPROVE],
      fees: [...MANAGE, ACTIONS.APPROVE],
      billing: [...MANAGE, ACTIONS.APPROVE],
      forms: [...MANAGE, ACTIONS.APPROVE],
      activities: MANAGE,
      cafeteria: MANAGE,
      assets: MANAGE,
      absences: MANAGE,
      emergency: MANAGE,
      facilities: [...MANAGE, ACTIONS.APPROVE],
      purchasing: MANAGE,
      visitors: [...MANAGE, ACTIONS.APPROVE],
      library: MANAGE,
      admissions: MANAGE,
      students: MANAGE,
      studentDemographics: MANAGE,
      guardians: MANAGE,
      households: MANAGE,
      enrollment: ALL,
      attendance: [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.PRINT, ACTIONS.EXPORT],
      calendar: ALL,
      documents: ALL,
      messages: CORRESPOND,
      announcements: [ACTIONS.VIEW, ACTIONS.CREATE],
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
    },
  },

  [ROLES.ATTENDANCE_CLERK]: {
    scope: SCOPES.SCHOOL,
    modules: {
      dismissal: [...MANAGE, ACTIONS.APPROVE],
      calendar: [ACTIONS.VIEW],
      students: VIEW_ONLY,
      // Chasing an unsubmitted register means knowing whose class it is.
      staff: VIEW_ONLY,
      attendance: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.OVERRIDE, ACTIONS.PRINT, ACTIONS.EXPORT],
      documents: [ACTIONS.VIEW, ACTIONS.UPLOAD],
      messages: CORRESPOND,
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
    },
  },

  [ROLES.BILLING_ENROLLMENT_ADMIN]: {
    scope: SCOPES.SCHOOL,
    modules: {
      calendar: [ACTIONS.VIEW],
      // Accounts payable. Enters invoices and assembles payment batches;
      // APPROVE is deliberately absent on payables, so the hand that keys an
      // invoice is never the hand that releases the money for it.
      finance: VIEW_PRINT_EXPORT,
      financeBudget: VIEW_ONLY,
      financeRequests: VIEW_ONLY,
      financePayables: MANAGE,
      financeLedger: VIEW_PRINT_EXPORT,
      financeReconciliation: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT],
      students: VIEW_ONLY,
      enrollment: ALL,
      applications: ALL,
      guardians: MANAGE,
      households: ALL,
      billing: ALL,
      fees: ALL,
      payments: ALL,
      documents: ALL,
      reports: VIEW_PRINT_EXPORT,
      messages: CORRESPOND,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
    },
  },

  /**
   * Counselors are school-scoped, not caseload-scoped.
   *
   * The caseload is derived from the interventions assigned to them, which
   * made the original caseload scope circular: a counselor with no
   * interventions could reach no students, and so could never create the
   * first one. More importantly it was wrong about the job — a counselor at a
   * school legitimately looks up any student there. What the caseload governs
   * is whose *counseling records* they own, and that is enforced by the
   * `confidential` list below rather than by hiding the student.
   */
  [ROLES.EMIS_COORDINATOR]: {
    scope: SCOPES.DISTRICT,
    modules: {
      // State reporting end to end. Everything else is read to trace a
      // reported value to its source; corrections happen in the owning
      // module, by the people who own it.
      stateReporting: [...ALL],
      students: VIEW_PRINT_EXPORT,
      studentDemographics: VIEW_ONLY,
      enrollment: VIEW_ONLY,
      staff: VIEW_ONLY,
      courses: VIEW_ONLY,
      sections: VIEW_ONLY,
      schedules: [ACTIONS.VIEW],
      attendance: VIEW_ONLY,
      assessments: VIEW_ONLY,
      calendar: VIEW_ONLY,
      messages: CORRESPOND,
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      documents: [ACTIONS.VIEW, ACTIONS.UPLOAD],
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
    },
  },
  [ROLES.TREASURER]: {
    scope: SCOPES.DISTRICT,
    modules: {
      // Finance and forecast state reporting, from the district's finance system.
      stateReporting: [ACTIONS.VIEW, ACTIONS.CREATE],
      fees: VIEW_PRINT_EXPORT,
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      messages: CORRESPOND,
      documents: [ACTIONS.VIEW, ACTIONS.UPLOAD],
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
    },
  },
  [ROLES.ASSESSMENT_COORDINATOR]: {
    scope: SCOPES.SCHOOL,
    modules: {
      // The testing office: everything in Assessments, and the context it
      // needs from elsewhere without editing it.
      assessments: [...ALL],
      curriculum: [ACTIONS.VIEW],
      courses: VIEW_ONLY,
      sections: VIEW_ONLY,
      schedules: [ACTIONS.VIEW],
      staff: VIEW_ONLY,
      students: VIEW_ONLY,
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      stateReporting: VIEW_ONLY,
      documents: [ACTIONS.VIEW, ACTIONS.UPLOAD],
      messages: CORRESPOND,
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      calendar: [ACTIONS.VIEW, ACTIONS.CREATE],
      forms: [ACTIONS.VIEW, ACTIONS.CREATE],
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
    },
  },
  [ROLES.CURRICULUM_DIRECTOR]: {
    scope: SCOPES.DISTRICT,
    modules: {
      // State reporting: the findings this role corrects at their source.
      stateReporting: VIEW_ONLY,
      curriculum: [...ALL],
      courses: VIEW_ONLY,
      sections: VIEW_ONLY,
      assessments: [ACTIONS.VIEW],
      assignments: [ACTIONS.VIEW],
      staff: VIEW_ONLY,
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      library: [ACTIONS.VIEW],
      documents: [ACTIONS.VIEW, ACTIONS.UPLOAD],
      messages: CORRESPOND,
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      calendar: [ACTIONS.VIEW],
      forms: [ACTIONS.VIEW, ACTIONS.CREATE],
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
    },
  },
  [ROLES.SCHOOL_COUNSELOR]: {
    scope: SCOPES.SCHOOL,
    modules: {
      calendar: [ACTIONS.VIEW, ACTIONS.CREATE],
      curriculum: [ACTIONS.VIEW],
      assessments: VIEW_PRINT_EXPORT,
      // The catalog: course planning is the counselor's daily work.
      courses: VIEW_ONLY,
      courseRequests: [...MANAGE, ACTIONS.APPROVE],
      forms: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.APPROVE],
      // Counselors author 504 plans, which also name providers and collectors.
      staff: VIEW_ONLY,
      students: VIEW_ONLY,
      counseling: ALL,
      activities: VIEW_ONLY,
      sessionNotes: ALL,
      interventions: ALL,
      grades: VIEW_PRINT_EXPORT,
      attendance: VIEW_PRINT_EXPORT,
      discipline: [ACTIONS.VIEW, ACTIONS.PRINT],
      schedules: [ACTIONS.VIEW, ACTIONS.EDIT],
      graduation: [...VIEW_PRINT_EXPORT, ACTIONS.EDIT],
      documents: [ACTIONS.VIEW, ACTIONS.UPLOAD],
      messages: CORRESPOND,
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      evaluations: [ACTIONS.VIEW],
    },
    confidential: ['counselingNotes'],
  },

  /**
   * Human Resources. District-wide, because people move between schools and
   * the record follows the person. The personnel record is theirs —
   * positions, credentials, contracts, documents, offboarding and the
   * accounts staff sign in with — but not student records: HR has no reason
   * to read a child's file.
   */
  [ROLES.HR]: {
    scope: SCOPES.DISTRICT,
    modules: {
      // State reporting: the findings this role corrects at their source.
      stateReporting: VIEW_ONLY,
      staff: ALL,
      users: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT],
      // HR owns leave policy, balances and restricted documents.
      absences: ALL,
      // HR owns templates, cycles, reassignment, reopen and restricted reports.
      evaluations: ALL,
      staffEvaluations: VIEW_ONLY,
      documents: MANAGE,
      forms: MANAGE,
      calendar: [ACTIONS.VIEW],
      messages: [ACTIONS.VIEW, ACTIONS.MESSAGE],
      notifications: [ACTIONS.VIEW],
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      schools: VIEW_ONLY,
      courses: [ACTIONS.VIEW],
      sections: [ACTIONS.VIEW],
      helper: [ACTIONS.VIEW],
    },
    confidential: ['staffPersonnel', 'staffCompensation'],
  },

  /** Payroll: compensation and the payroll export. No personal contact, notes or background. */
  [ROLES.PAYROLL]: {
    scope: SCOPES.DISTRICT,
    modules: {
      documents: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.UPLOAD, ACTIONS.PRINT],
      staff: VIEW_PRINT_EXPORT,
      absences: VIEW_PRINT_EXPORT,
      calendar: [ACTIONS.VIEW],
      messages: [ACTIONS.VIEW, ACTIONS.MESSAGE],
      notifications: [ACTIONS.VIEW],
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      schools: VIEW_ONLY,
      helper: [ACTIONS.VIEW],
    },
    confidential: ['staffCompensation'],
  },

  [ROLES.REGISTRAR]: {
    scope: SCOPES.SCHOOL,
    modules: {
      // Families: the registrar owns households, contacts, relationships and their rights, and records restrictions.
      guardians: ALL,
      households: ALL,
      calendar: [ACTIONS.VIEW, ACTIONS.CREATE],
      transportation: VIEW_PRINT_EXPORT,
      dismissal: [...MANAGE, ACTIONS.APPROVE],
      curriculum: [ACTIONS.VIEW],
      assessments: MANAGE,
      courseRequests: ALL,
      fees: MANAGE,
      forms: MANAGE,
      admissions: ALL,
      // Enrolling students is the registrar's job, so CREATE belongs here —
      // and without it the roster import they own would fail on its first row.
      students: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT, ACTIONS.EXPORT],
      studentDemographics: MANAGE,
      // A registrar builds the master schedule and assigns teachers to
      // sections, so they have to be able to see who the teachers are.
      staff: VIEW_PRINT_EXPORT,
      stateReporting: [ACTIONS.VIEW, ACTIONS.CREATE],
      enrollment: ALL,
      schedules: ALL,
      masterSchedule: ALL,
      sections: ALL,
      courses: ALL,
      transcripts: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.PRINT, ACTIONS.EXPORT, ACTIONS.APPROVE],
      credits: [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.PRINT],
      graduation: ALL,
      grades: [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.PRINT, ACTIONS.EXPORT, ACTIONS.OVERRIDE],
      gradeOverrides: [ACTIONS.VIEW, ACTIONS.APPROVE],
      documents: ALL,
      reports: VIEW_PRINT_EXPORT,
      // The registrar owns enrolment data, so they own bulk enrolment loading.
      dataImport: [ACTIONS.VIEW, ACTIONS.CREATE],
      dataExport: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      evaluations: [ACTIONS.VIEW],
    },
  },

  /** School-scoped for the same reason as the counselor, above. */
  [ROLES.IEP_COORDINATOR]: {
    scope: SCOPES.SCHOOL,
    modules: {
      // State reporting: the findings this role corrects at their source.
      stateReporting: VIEW_ONLY,
      calendar: [ACTIONS.VIEW, ACTIONS.CREATE],
      forms: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.APPROVE],
      students: VIEW_ONLY,
      iep: ALL,
      // Naming providers and collectors on a plan requires seeing who exists.
      staff: VIEW_ONLY,
      interventions: ALL,
      grades: VIEW_PRINT_EXPORT,
      attendance: VIEW_PRINT_EXPORT,
      documents: ALL,
      messages: CORRESPOND,
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
    },
    confidential: ['iepDetail'],
  },

  /** School-scoped for the same reason as the counselor, above. */
  [ROLES.SPECIAL_SERVICES]: {
    scope: SCOPES.SCHOOL,
    modules: {
      calendar: [ACTIONS.VIEW],
      students: VIEW_ONLY,
      iep: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT],
      interventions: ALL,
      grades: VIEW_ONLY,
      attendance: VIEW_ONLY,
      documents: [ACTIONS.VIEW, ACTIONS.UPLOAD],
      messages: CORRESPOND,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
    },
    confidential: ['iepDetail'],
  },

  /** The district transportation office: routes, riders, vehicles, drivers, trips, requests and alerts. */
  [ROLES.TRANSPORTATION_OPERATIONS]: {
    scope: SCOPES.DISTRICT,
    modules: {
      documents: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.UPLOAD, ACTIONS.PRINT],
      operations: [ACTIONS.VIEW, ACTIONS.CREATE],
      absences: [ACTIONS.VIEW],
      staff: [ACTIONS.VIEW],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW],
      activities: [ACTIONS.VIEW],
      announcements: [ACTIONS.VIEW, ACTIONS.CREATE],
      calendar: [ACTIONS.VIEW, ACTIONS.CREATE],
      // A department budget owner: sees the lines they are accountable for
      // and asks for more when they run short.
      finance: VIEW_ONLY,
      financeBudget: VIEW_ONLY,
      financeRequests: [ACTIONS.VIEW, ACTIONS.CREATE],
      students: [ACTIONS.VIEW],
      transportation: ALL,
      dismissal: [ACTIONS.VIEW, ACTIONS.APPROVE],
      reports: VIEW_PRINT_EXPORT,
      messages: CORRESPOND,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
    },
  },

  /** District operations: sees every school's operational state; reads source modules' summaries, not their confidential detail. */
  [ROLES.OPERATIONS_MANAGER]: {
    scope: SCOPES.DISTRICT,
    modules: {
      documents: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.UPLOAD, ACTIONS.PRINT],
      operations: ALL,
      facilities: ALL,
      transportation: [ACTIONS.VIEW, ACTIONS.EDIT],
      dismissal: [ACTIONS.VIEW],
      cafeteria: [ACTIONS.VIEW],
      visitors: [ACTIONS.VIEW],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      assets: [ACTIONS.VIEW],
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      calendar: [ACTIONS.VIEW, ACTIONS.CREATE],
      schools: VIEW_ONLY,
      staff: [ACTIONS.VIEW],
      reports: VIEW_PRINT_EXPORT,
      messages: CORRESPOND,
      notifications: [ACTIONS.VIEW],
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
    },
  },

  /**
   * Facilities management across the district: triages requests, sets
   * priority, assigns, verifies, runs PM and inspections, owns the settings.
   * `facilities:approve` is what separates a manager from a technician.
   */
  [ROLES.FACILITIES_MANAGER]: {
    scope: SCOPES.DISTRICT,
    modules: {
      documents: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.UPLOAD, ACTIONS.PRINT],
      facilities: ALL,
      operations: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT],
      assets: [ACTIONS.VIEW],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      // Pre-registers a vendor's visit for a work order; Visitors still screens and checks them in.
      visitors: [ACTIONS.VIEW, ACTIONS.CREATE],
      absences: [ACTIONS.VIEW],
      calendar: [ACTIONS.VIEW],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      schools: VIEW_ONLY,
      staff: [ACTIONS.VIEW],
      reports: VIEW_PRINT_EXPORT,
      messages: CORRESPOND,
      notifications: [ACTIONS.VIEW],
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
    },
  },

  /** Custodial and facilities staff at a school: the checks, tasks and work orders of the day. */
  [ROLES.FACILITIES_STAFF]: {
    scope: SCOPES.SCHOOL,
    modules: {
      operations: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT],
      assets: [ACTIONS.VIEW],
      // A technician sends a missing part to Purchasing as a draft request; Purchasing approves and orders.
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      calendar: [ACTIONS.VIEW],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      messages: CORRESPOND,
      notifications: [ACTIONS.VIEW],
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
    },
  },

  /**
   * A bus driver: their own routes, trips and operational manifests only
   * (Transportation's driver mode checks the role and the assignment). No
   * student, health, grade or discipline module at all.
   */
  [ROLES.BUS_DRIVER]: {
    scope: SCOPES.DISTRICT,
    modules: {
      messages: CORRESPOND,
      notifications: [ACTIONS.VIEW],
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW],
      ownProfile: MANAGE,
    },
  },

  /** The circulation desk: library circulation and patron lookup; no catalog editing, acquisitions or settings (checked in Library). */
  [ROLES.LIBRARY_ASSISTANT]: {
    scope: SCOPES.SCHOOL,
    modules: {
      calendar: [ACTIONS.VIEW],
      students: [ACTIONS.VIEW],
      library: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT],
      printCenter: VIEW_ONLY,
      messages: CORRESPOND,
      notifications: [ACTIONS.VIEW],
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW],
    },
  },

  /**
   * District property across every school: the registry with costs and
   * funding, transfers, inventory, labels, loss and damage, retirement and
   * disposal approval, settings. `assets:approve` is what separates a manager.
   */
  [ROLES.ASSET_MANAGER]: {
    scope: SCOPES.DISTRICT,
    modules: {
      documents: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.UPLOAD, ACTIONS.PRINT],
      assets: ALL,
      students: [ACTIONS.VIEW],
      staff: [ACTIONS.VIEW],
      fees: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      forms: [ACTIONS.VIEW],
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      schools: VIEW_ONLY,
      messages: CORRESPOND,
      notifications: [ACTIONS.VIEW],
      calendar: [ACTIONS.VIEW],
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      ownProfile: MANAGE,
    },
  },

  /** A school's technology staff: device distribution and collection, repairs, labels, inventory. */
  [ROLES.TECHNOLOGY_STAFF]: {
    scope: SCOPES.SCHOOL,
    modules: {
      assets: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT, ACTIONS.EXPORT],
      students: [ACTIONS.VIEW],
      staff: [ACTIONS.VIEW],
      fees: [ACTIONS.VIEW, ACTIONS.CREATE],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      reports: [ACTIONS.VIEW],
      printCenter: VIEW_ONLY,
      messages: CORRESPOND,
      notifications: [ACTIONS.VIEW],
      calendar: [ACTIONS.VIEW],
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      ownProfile: MANAGE,
    },
  },

  [ROLES.LIBRARY_MEDIA_SPECIALIST]: {
    scope: SCOPES.SCHOOL,
    modules: {
      calendar: [ACTIONS.VIEW, ACTIONS.CREATE],
      // A department budget owner: sees the lines they are accountable for
      // and asks for more when they run short.
      finance: VIEW_ONLY,
      financeBudget: VIEW_ONLY,
      financeRequests: [ACTIONS.VIEW, ACTIONS.CREATE],
      students: [ACTIONS.VIEW],
      library: ALL,
      assets: MANAGE,
      fees: [ACTIONS.VIEW, ACTIONS.CREATE],
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      evaluations: [ACTIONS.VIEW],
    },
  },

  [ROLES.MEDICAL_STAFF]: {
    scope: SCOPES.SCHOOL,
    modules: {
      calendar: [ACTIONS.VIEW],
      students: VIEW_ONLY,
      medical: ALL,
      medications: ALL,
      immunizations: ALL,
      documents: [ACTIONS.VIEW, ACTIONS.UPLOAD],
      attendance: [ACTIONS.VIEW, ACTIONS.CREATE],
      messages: CORRESPOND,
      reports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      ownProfile: MANAGE,
      absences: [ACTIONS.VIEW, ACTIONS.CREATE],
      facilities: [ACTIONS.VIEW, ACTIONS.CREATE],
      purchasing: [ACTIONS.VIEW, ACTIONS.CREATE],
      emergency: [ACTIONS.VIEW, ACTIONS.CREATE],
      evaluations: [ACTIONS.VIEW],
    },
    confidential: ['medicalDetail'],
  },

  /**
   * An invited tester, working inside the demo school.
   *
   * They can do the job rather than only look at it: create an assignment,
   * grade it, correct attendance, add a referral, change a schedule. That
   * reverses an earlier read-only design, deliberately — feedback on a
   * workflow from somebody who was not allowed to finish it is feedback on a
   * screenshot. What makes write access safe is not prohibition but
   * recoverability: `Reset Demo Environment` restores the dataset while
   * preserving tester accounts, their feedback and their progress.
   *
   * It stays narrower than a school administrator in two deliberate ways.
   *
   * Nothing destructive or final: no DELETE, no APPROVE, no OVERRIDE. A tester
   * may post a grade and may not approve a grade change or reopen a closed
   * grading period, because those exist to be reviewed by someone accountable,
   * and a tester is accountable to nobody here. A reset restores records; it
   * does not un-approve a decision.
   *
   * No confidential areas, and no counselling or medical module at all. These
   * students are invented, but a tester has no professional reason to read a
   * health record, and the habit of granting it because the data is fake is
   * exactly how it comes to be granted when the data is not.
   */
  [ROLES.FOCUS_GROUP_TESTER]: {
    scope: SCOPES.SCHOOL,
    modules: {
      schools: VIEW_ONLY,
      students: MANAGE,
      studentDemographics: MANAGE,
      guardians: MANAGE,
      households: MANAGE,
      staff: VIEW_PRINT_EXPORT,
      enrollment: MANAGE,
      applications: MANAGE,
      courses: MANAGE,
      sections: MANAGE,
      schedules: MANAGE,
      masterSchedule: MANAGE,
      attendance: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT, ACTIONS.EXPORT],
      gradebook: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT],
      assignments: MANAGE,
      grades: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.PRINT, ACTIONS.EXPORT],
      transcripts: VIEW_PRINT_EXPORT,
      credits: VIEW_ONLY,
      graduation: VIEW_PRINT_EXPORT,
      discipline: MANAGE,
      interventions: MANAGE,
      documents: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.UPLOAD, ACTIONS.PRINT],
      transportation: VIEW_ONLY,
      dismissal: VIEW_ONLY,
      library: VIEW_ONLY,
      calendar: MANAGE,
      announcements: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT],
      messages: CORRESPOND,
      reports: VIEW_PRINT_EXPORT,
      customReports: VIEW_PRINT_EXPORT,
      printCenter: VIEW_ONLY,
      dataExport: VIEW_PRINT_EXPORT,
      // The reason they are here.
      feedbackLab: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.UPLOAD],
    },
  },

  [ROLES.STUDENT]: {
    scope: SCOPES.SELF,
    modules: {
      assessments: [ACTIONS.VIEW],
      graduation: [ACTIONS.VIEW],
      activities: VIEW_ONLY,
      // The catalog is not confidential; requesting needs browsing.
      courses: [ACTIONS.VIEW],
      courseRequests: [ACTIONS.VIEW, ACTIONS.CREATE],
      fees: [ACTIONS.VIEW],
      ownProfile: VIEW_ONLY,
      ownGrades: VIEW_ONLY,
      ownAttendance: VIEW_ONLY,
      ownAssignments: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.UPLOAD, ACTIONS.PRINT],
      ownSchedule: VIEW_PRINT_EXPORT,
      messages: CORRESPOND,
      announcements: [ACTIONS.VIEW],
      calendar: [ACTIONS.VIEW],
      printCenter: VIEW_ONLY,
    },
  },

  [ROLES.PARENT_GUARDIAN]: {
    scope: SCOPES.HOUSEHOLD,
    modules: {
      assessments: [ACTIONS.VIEW],
      graduation: [ACTIONS.VIEW],
      activities: VIEW_ONLY,
      childProfile: VIEW_ONLY,
      childGrades: VIEW_ONLY,
      childAttendance: VIEW_ONLY,
      childAssignments: VIEW_ONLY,
      childDocuments: [ACTIONS.VIEW, ACTIONS.UPLOAD, ACTIONS.PRINT],
      billing: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.PRINT],
      fees: [ACTIONS.VIEW, ACTIONS.PRINT],
      payments: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.PRINT],
      messages: CORRESPOND,
      announcements: [ACTIONS.VIEW],
      calendar: [ACTIONS.VIEW],
      ownProfile: MANAGE,
      printCenter: VIEW_ONLY,
    },
  },
}

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && value in ROLE_PERMISSIONS
}

/**
 * The identity an authorization decision is made about.
 *
 * Assembled by the API from the session and the user's own record — never from
 * anything the client sends. `assignedSectionIds` and `caseloadStudentIds` are
 * what turn a role's declared scope into an actual filter.
 */
export interface Principal {
  userId: string
  tenantId: string
  role: Role
  districtId?: string | null
  /** Schools the user is attached to. A district role may hold several. */
  schoolIds: readonly string[]
  /** Sections a teacher is teacher or co-teacher of. */
  assignedSectionIds?: readonly string[]
  /** Students on a counselor's / coordinator's caseload. */
  caseloadStudentIds?: readonly string[]
  /** Students a guardian is recorded against, with portal access granted. */
  guardianStudentIds?: readonly string[]
  /** Per child, the rights in effect today for a guardian (Families). A child absent here is not reachable. */
  guardianRights?: Readonly<Record<string, readonly string[]>>
  /** The student's own record id, for the student portal. */
  studentId?: string | null
  /** Dated substitute coverage, for the temporary-assignment scope. */
  substituteAssignments?: readonly { sectionId: string; date: string }[]
  /** Set when an administrator is viewing as another user. */
  impersonatedBy?: string | null
  /** Additional roles in effect today (dated, scoped assignments). Their grants add to the primary role's. */
  additionalRoles?: readonly Role[]
}

// ─────────────────────────────────────────────────────────────────────────────
// The Developer
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The role the people who build the product hold. Stored as `system_admin`
 * (the key every existing account record already carries) and shown as
 * "Developer".
 *
 * It sits above every administrator. The matrix above describes what each
 * school role may do; this role is defined instead by the one thing it cannot
 * be — stopped. `can()` and `hasConfidentialAccess()` answer yes for it before
 * consulting the matrix, every scope resolves to everything, and the ad-hoc
 * role lists in the API ask `isDeveloper` before their own list. The audit
 * trail still records every read and write it makes; that, not a narrower
 * grant, is the accountability.
 */
export const DEVELOPER_ROLE: Role = ROLES.SYSTEM_ADMIN

/** Is this the Developer role? Takes anything, so a stored string can be asked directly. */
export function isDeveloper(role: unknown): boolean {
  return role === DEVELOPER_ROLE
}

/** Does this principal hold the Developer role, as the primary or an active additional role? */
export function isDeveloperPrincipal(principal: Pick<Principal, 'role' | 'additionalRoles'> | null | undefined): boolean {
  if (!principal) return false
  return isDeveloper(principal.role) || (principal.additionalRoles ?? []).some(isDeveloper)
}

/** Every module with every action: the Developer's grant, written out for the client. */
const EVERY_GRANT: Partial<Record<Module, readonly Action[]>> = Object.fromEntries(
  MODULES.map((module) => [module, Object.values(ACTIONS) as readonly Action[]]),
)

/** Does this principal's role grant `action` on `module` at all? */
export function can(principal: Principal | null | undefined, module: Module, action: Action): boolean {
  if (!principal || !isRole(principal.role)) return false
  // The Developer passes every check — one short-circuit here rather than an
  // entry in every role list.
  if (isDeveloperPrincipal(principal)) return true
  const granted = ROLE_PERMISSIONS[principal.role].modules[module]
  if (granted?.includes(action)) return true
  // An additional role in effect today adds its grants; it never takes any away.
  return (principal.additionalRoles ?? []).some((r) => isRole(r) && ROLE_PERMISSIONS[r].modules[module]?.includes(action))
}

/** The union of grants across a primary role and its active additional roles. */
export function grantsForRoles(role: Role, additional: readonly Role[] = []): Partial<Record<Module, readonly Action[]>> {
  if ([role, ...additional].some(isDeveloper)) return EVERY_GRANT
  const out: Partial<Record<Module, Action[]>> = {}
  for (const r of [role, ...additional]) for (const [m, acts] of Object.entries(ROLE_PERMISSIONS[r]?.modules ?? {})) out[m as Module] = [...new Set([...(out[m as Module] ?? []), ...(acts as Action[])])]
  return out
}

/** Every module/action pair a role holds. Sent to the client to drive the UI. */
export function grantsFor(role: Role): Partial<Record<Module, readonly Action[]>> {
  if (isDeveloper(role)) return EVERY_GRANT
  return ROLE_PERMISSIONS[role]?.modules ?? {}
}

export function scopeFor(role: Role): Scope | null {
  return ROLE_PERMISSIONS[role]?.scope ?? null
}

export function hasConfidentialAccess(
  principal: Principal | null | undefined,
  area: 'counselingNotes' | 'medicalDetail' | 'disciplineDetail' | 'iepDetail' | 'financeSensitive' | 'staffPersonnel' | 'staffCompensation',
): boolean {
  if (!principal || !isRole(principal.role)) return false
  if (isDeveloperPrincipal(principal)) return true
  return [principal.role, ...(principal.additionalRoles ?? [])].some((r) => isRole(r) && (ROLE_PERMISSIONS[r].confidential?.includes(area) ?? false))
}

// ─────────────────────────────────────────────────────────────────────────────
// Record-level scope
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A filter the data layer can apply, derived from the principal's scope.
 *
 * `kind: 'all'` means no narrowing beyond the tenant — which the tenant guard
 * applies unconditionally, so it is still not "everything".
 */
export type ScopeFilter =
  | { kind: 'all' }
  | { kind: 'none' }
  | { kind: 'schools'; schoolIds: readonly string[] }
  | { kind: 'sections'; sectionIds: readonly string[] }
  | { kind: 'students'; studentIds: readonly string[] }

/**
 * Turns a principal's declared scope into the concrete set of records they may
 * reach, for a module that is about students.
 *
 * Returning `{ kind: 'none' }` for an empty set is deliberate and load-bearing:
 * a counselor with no caseload yet sees no students, not every student. The
 * previous build's equivalent would have fallen through to an unfiltered read.
 */
export function resolveStudentScope(principal: Principal, onDate?: string): ScopeFilter {
  switch (scopeFor(principal.role)) {
    case SCOPES.PLATFORM:
      return { kind: 'all' }

    case SCOPES.DISTRICT:
      // Still tenant-bound; the tenant guard handles that half.
      return { kind: 'all' }

    case SCOPES.SCHOOL:
      return principal.schoolIds.length > 0
        ? { kind: 'schools', schoolIds: principal.schoolIds }
        : { kind: 'none' }

    case SCOPES.ASSIGNED_SECTIONS: {
      const sections = principal.assignedSectionIds ?? []
      return sections.length > 0 ? { kind: 'sections', sectionIds: sections } : { kind: 'none' }
    }

    case SCOPES.TEMPORARY_ASSIGNMENT: {
      // A substitute's access is bounded by date as well as section. Cover for
      // Tuesday does not grant a roster on Wednesday.
      const assignments = principal.substituteAssignments ?? []
      const day = onDate ?? new Date().toISOString().slice(0, 10)
      const sectionIds = assignments.filter((a) => a.date === day).map((a) => a.sectionId)
      return sectionIds.length > 0 ? { kind: 'sections', sectionIds } : { kind: 'none' }
    }

    case SCOPES.CASELOAD: {
      const caseload = principal.caseloadStudentIds ?? []
      return caseload.length > 0 ? { kind: 'students', studentIds: caseload } : { kind: 'none' }
    }

    case SCOPES.HOUSEHOLD: {
      const children = principal.guardianStudentIds ?? []
      return children.length > 0 ? { kind: 'students', studentIds: children } : { kind: 'none' }
    }

    case SCOPES.SELF:
      return principal.studentId ? { kind: 'students', studentIds: [principal.studentId] } : { kind: 'none' }

    default:
      return { kind: 'none' }
  }
}

/**
 * Whether a principal may reach one specific student.
 *
 * The API calls this on every single-student route. It takes the student's
 * school so a school-scoped role is checked against the school the student is
 * actually enrolled in, rather than merely sharing a tenant.
 */
export function canReachStudent(
  principal: Principal,
  student: { id: string; schoolId?: string | null },
  onDate?: string,
): boolean {
  const filter = resolveStudentScope(principal, onDate)
  switch (filter.kind) {
    case 'all':
      return true
    case 'none':
      return false
    case 'schools':
      return student.schoolId != null && filter.schoolIds.includes(student.schoolId)
    case 'students':
      return filter.studentIds.includes(student.id)
    case 'sections':
      // Section membership cannot be decided from the student alone; the caller
      // resolves the roster and uses `canReachStudentInSections` instead.
      // Returning false here keeps the unsafe default closed.
      return false
  }
}

/** Section-scoped variant, for teachers and substitutes. */
export function canReachStudentInSections(
  principal: Principal,
  studentSectionIds: readonly string[],
  onDate?: string,
): boolean {
  const filter = resolveStudentScope(principal, onDate)
  switch (filter.kind) {
    case 'all':
      return true
    case 'none':
      return false
    case 'sections':
      return studentSectionIds.some((id) => filter.sectionIds.includes(id))
    case 'students':
    case 'schools':
      return false
  }
}

/** Whether a teacher may act on a given section at all. */
export function canReachSection(principal: Principal, sectionId: string, onDate?: string): boolean {
  const scope = scopeFor(principal.role)
  if (scope === SCOPES.PLATFORM || scope === SCOPES.DISTRICT || scope === SCOPES.SCHOOL) return true
  if (scope === SCOPES.ASSIGNED_SECTIONS) return (principal.assignedSectionIds ?? []).includes(sectionId)
  if (scope === SCOPES.TEMPORARY_ASSIGNMENT) {
    const day = onDate ?? new Date().toISOString().slice(0, 10)
    return (principal.substituteAssignments ?? []).some((a) => a.sectionId === sectionId && a.date === day)
  }
  return false
}
